import {debugWarn} from './debug';

/**
 * Local persistence for project preferences that the backend does not store yet.
 *
 * On the desktop app these lived in `settings.json` behind the Rust commands
 * `get_current_project_id` / `set_current_project_id` / `get_project_metadata` /
 * `update_project_metadata`.
 *
 * `favorite` and `lastActivity` are requested as real columns on the project record — see
 * docs/BACKEND_REQUESTS.md. Once that lands, replace the bodies here with API calls; the thunks
 * calling them do not need to change.
 *
 * "Current project" legitimately stays client-side: on the web it is derived from the URL, and this
 * is only the "where was I last" fallback for a cold load.
 */

const CURRENT_PROJECT_KEY = 'censorme.currentProjectId';
const PROJECT_METADATA_KEY = 'censorme.projectMetadata';

export interface ProjectMetadata {
  projectId: string;
  favorite: boolean;
  lastActivity: string | null;
}

export function getCurrentProjectId(): string | null {
  try {
    return localStorage.getItem(CURRENT_PROJECT_KEY);
  } catch (error) {
    debugWarn('[projectPreferences] Could not read current project id:', error);
    return null;
  }
}

export function setCurrentProjectId(projectId: string | null): void {
  try {
    if (projectId) {
      localStorage.setItem(CURRENT_PROJECT_KEY, projectId);
    } else {
      localStorage.removeItem(CURRENT_PROJECT_KEY);
    }
  } catch (error) {
    debugWarn('[projectPreferences] Could not persist current project id:', error);
  }
}

/**
 * Forget the outgoing session's project selection.
 *
 * These keys are not namespaced by user — they cannot be, the current project is chosen before the
 * profile is read — so leaving them in place hands the next account to sign in on this browser a
 * project id it does not own. Every project-scoped fetch then comes back `403 You do not own this
 * project`. Called from `clearSessionScopedCaches` on sign-out.
 */
export function clearProjectPreferences(): void {
  try {
    localStorage.removeItem(CURRENT_PROJECT_KEY);
    localStorage.removeItem(PROJECT_METADATA_KEY);
  } catch (error) {
    debugWarn('[projectPreferences] Could not clear project preferences:', error);
  }
}

export function getProjectMetadata(): ProjectMetadata[] {
  try {
    const raw = localStorage.getItem(PROJECT_METADATA_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as ProjectMetadata[]) : [];
  } catch (error) {
    debugWarn('[projectPreferences] Could not read project metadata:', error);
    return [];
  }
}

export function updateProjectMetadata(
  projectId: string,
  changes: {favorite?: boolean; lastActivity?: string}
): ProjectMetadata {
  const all = getProjectMetadata();
  const existing = all.find(m => m.projectId === projectId);

  const updated: ProjectMetadata = {
    projectId,
    favorite: changes.favorite !== undefined ? changes.favorite : (existing?.favorite ?? false),
    lastActivity: changes.lastActivity !== undefined ? changes.lastActivity : (existing?.lastActivity ?? null),
  };

  const next = existing
    ? all.map(m => (m.projectId === projectId ? updated : m))
    : [...all, updated];

  try {
    localStorage.setItem(PROJECT_METADATA_KEY, JSON.stringify(next));
  } catch (error) {
    debugWarn('[projectPreferences] Could not persist project metadata:', error);
  }

  return updated;
}
